// lib/dates.ts — the ONE place UI-facing dates get formatted.
//
// Every formatter here is pinned to en-GB + Europe/London. The pin is not
// cosmetic: production renders on a UTC server while users' browsers run in
// Europe/London, so any formatter that falls back to the system timezone
// produces different text on the server than on the client. During British
// Summer Time that text difference makes React hydration fail (minified
// error #418 — seen live on /documents/[documentId], PostHog issue
// 019f5c9c-3012-7c41-b4f1-0b2f58dfd835). Omitting the locale is worse still:
// the server's ICU default (en-US) disagrees with a UK browser year-round.
//
// Direct use of toLocaleDateString / toLocaleTimeString / Intl.DateTimeFormat
// (and locale-argument toLocaleString) outside this file is rejected by
// __tests__/guardrails/pinned-date-formatting.test.ts. Add a preset here or
// use formatUk instead.
//
// lib/briefing/dates.ts is deliberately separate: briefing day-bucketing is
// pinned to UTC to stay aligned with cron windows, not to user-facing time.

export const UK_TIMEZONE = "Europe/London";
export const UK_LOCALE = "en-GB";
export type DateInput = Date | string | number | null | undefined;

const formatterCache = new Map<string, Intl.DateTimeFormat>();

function getFormatter(opts: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = JSON.stringify(opts, Object.keys(opts).sort());
  let formatter = formatterCache.get(key);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(UK_LOCALE, {
      ...opts,
      timeZone: UK_TIMEZONE,
    });
    formatterCache.set(key, formatter);
  }
  return formatter;
}

function toDate(input: DateInput): Date | null {
  if (input === null || input === undefined || input === "") return null;
  const d = input instanceof Date ? input : new Date(input);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Format a date with arbitrary Intl options, always pinned to en-GB +
 * Europe/London (a caller-supplied `timeZone`/locale cannot unpin it).
 * Invalid or empty input returns `fallback`.
 */
export function formatUk(
  date: DateInput,
  opts: Intl.DateTimeFormatOptions,
  fallback = "—",
): string {
  const d = toDate(date);
  if (!d) return fallback;
  return getFormatter(opts).format(d);
}

/**
 * "2026-08-05" — the calendar date in Europe/London, in the shape
 * `<input type="date">` expects.
 *
 * Use this instead of `date.toISOString().slice(0, 10)` whenever the value
 * seeds a date input that a user can save back. The UTC slice disagrees with
 * every UK-pinned LABEL for a timestamp late in a BST evening: an entry stored
 * at 2026-08-04T23:30Z displays as 5 August but slices to "2026-08-04", so
 * saving any unrelated change silently moves the record back a day.
 */
export function ukDateInputValue(date: DateInput, fallback = ""): string {
  const d = toDate(date);
  if (!d) return fallback;
  const parts = getFormatter({
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(d);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? "";
  const year = part("year");
  const month = part("month");
  const day = part("day");
  return year && month && day ? `${year}-${month}-${day}` : fallback;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * How far UK wall-clock time runs ahead of UTC at a given instant: 0 in GMT,
 * +1h during BST. Derived from the pinned formatter rather than a table, so DST
 * transitions come from ICU.
 */
function ukOffsetMsAt(ms: number): number {
  // Whole seconds only — the parts below carry no milliseconds, so comparing
  // against a sub-second instant would skew the offset by up to 999ms.
  const base = Math.floor(ms / 1000) * 1000;
  const parts = getFormatter({
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(new Date(base));
  const n = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((p) => p.type === type)?.value ?? "0");
  const asIfUtc = Date.UTC(
    n("year"),
    n("month") - 1,
    n("day"),
    // en-GB hour12:false renders midnight as "24" in some ICU versions.
    n("hour") % 24,
    n("minute"),
    n("second"),
  );
  return asIfUtc - base;
}

/** Resolve a UK wall-clock instant (expressed as if it were UTC) back to UTC. */
function ukWallToUtc(wallMs: number, hintOffset: number): number {
  const utc = wallMs - hintOffset;
  const actual = ukOffsetMsAt(utc);
  return actual === hintOffset ? utc : wallMs - actual;
}

/**
 * The first instant of the Europe/London calendar day an instant falls in.
 *
 * Date-only input (`<input type="date">`) means a UK calendar day, not a UTC
 * one. Treating "2026-08-20" as `2026-08-20T00:00:00Z` is an hour into BST, so
 * the day it names starts an hour late.
 */
export function startOfUkDay(date: DateInput): Date | null {
  const d = toDate(date);
  if (!d) return null;
  const offset = ukOffsetMsAt(d.getTime());
  const wall = d.getTime() + offset;
  const dayStartWall = Math.floor(wall / DAY_MS) * DAY_MS;
  return new Date(ukWallToUtc(dayStartWall, offset));
}

/**
 * The last instant of the Europe/London calendar day an instant falls in.
 *
 * This is the one a deadline needs. `23:59:59.999Z` is NOT the end of a UK day
 * during BST — it is 00:59 the following morning, so a "valid until 20 Aug"
 * certificate stored that way both stays valid an hour too long AND renders as
 * "21 Aug" through every formatter above.
 */
export function endOfUkDay(date: DateInput): Date | null {
  const start = startOfUkDay(date);
  if (!start) return null;
  const offset = ukOffsetMsAt(start.getTime());
  const nextDayStartWall = start.getTime() + offset + DAY_MS;
  return new Date(ukWallToUtc(nextDayStartWall, offset) - 1);
}

/**
 * Advance by whole Europe/London CALENDAR days.
 *
 * `+ n * 24h` is not the same thing. The clock-change days are 23 and 25 hours
 * long, so adding fixed hours across one lands at the wrong wall-clock time and
 * — combined with an end-of-day boundary — can land on the wrong calendar day
 * entirely. Recurrence intervals ("re-inspect every 180 days") are calendar
 * intervals, so they belong here.
 */
export function addUkDays(date: DateInput, days: number): Date | null {
  const d = toDate(date);
  if (!d) return null;
  const offset = ukOffsetMsAt(d.getTime());
  const wall = d.getTime() + offset + days * DAY_MS;
  return new Date(ukWallToUtc(wall, offset));
}

/**
 * Whole Europe/London calendar days from `from` to `to` (can be negative).
 *
 * Floors on calendar-day boundaries, not on 24h buckets — the clock-change
 * days are 23/25 hours, so `(to - from) / DAY_MS` drifts by a day around
 * them. Used for "age" and "days overdue" displays (spec §10.2, §10.3) where
 * the answer must be a whole-day count a person reads the same way regardless
 * of which side of a clock change the two instants fall on.
 */
export function ukWholeDaysBetween(from: DateInput, to: DateInput): number | null {
  const a = startOfUkDay(from);
  const b = startOfUkDay(to);
  if (!a || !b) return null;
  return Math.round((b.getTime() - a.getTime()) / DAY_MS);
}

/**
 * The first and last instant of the Europe/London calendar MONTH an instant
 * falls in. The `from`/`to` a report defaults to when no range is given, in the
 * timezone the user reads it in — not the UTC month the server happens to sit
 * in during the first UK hour of a new month.
 */
export function ukMonthRange(date: DateInput): { from: Date; to: Date } | null {
  const d = toDate(date);
  if (!d) return null;
  const parts = getFormatter({ year: "numeric", month: "2-digit" }).formatToParts(d);
  const year = Number(parts.find((p) => p.type === "year")?.value);
  const month = Number(parts.find((p) => p.type === "month")?.value);
  if (!year || !month) return null;
  const nextYear = month === 12 ? year + 1 : year;
  const nextMonth = month === 12 ? 1 : month + 1;
  const pad = (n: number) => String(n).padStart(2, "0");
  const from = startOfUkDay(`${year}-${pad(month)}-01`);
  const startOfNextMonth = startOfUkDay(`${nextYear}-${pad(nextMonth)}-01`);
  if (!from || !startOfNextMonth) return null;
  return { from, to: new Date(startOfNextMonth.getTime() - 1) };
}

/**
 * A range-boundary label: the UK date alone when the instant sits exactly on a
 * UK day boundary (the first instant of a day, or the last — either end of an
 * `endOfUkDay` range), the full UK date-time otherwise. Date-only exports stay
 * clean ("1 Jul 2026") while a caller-supplied partial-day boundary
 * ("…T12:00:00Z") cannot masquerade as covering the whole day.
 */
export function ukRangeBoundaryLabel(date: DateInput, fallback = "—"): string {
  const d = toDate(date);
  if (!d) return fallback;
  const t = d.getTime();
  const onBoundary =
    startOfUkDay(d)?.getTime() === t || endOfUkDay(d)?.getTime() === t;
  return onBoundary ? ukDate(d) : ukDateTime(d);
}

/** "13/07/2026" */
export function ukShortDate(date: DateInput, fallback = "—"): string {
  return formatUk(date, { dateStyle: "short" }, fallback);
}

/** "13 Jul 2026" */
export function ukDate(date: DateInput, fallback = "—"): string {
  return formatUk(date, { day: "numeric", month: "short", year: "numeric" }, fallback);
}

/** "13 July 2026" */
export function ukLongDate(date: DateInput, fallback = "—"): string {
  return formatUk(date, { day: "numeric", month: "long", year: "numeric" }, fallback);
}

/** "13 Jul 2026, 18:52" */
export function ukDateTime(date: DateInput, fallback = "—"): string {
  return formatUk(date, { dateStyle: "medium", timeStyle: "short" }, fallback);
}

/** "13/07/2026, 18:52" */
export function ukShortDateTime(date: DateInput, fallback = "—"): string {
  return formatUk(date, { dateStyle: "short", timeStyle: "short" }, fallback);
}

/** "18:52" (24-hour) */
export function ukTime(date: DateInput, fallback = "—"): string {
  return formatUk(date, { hour: "2-digit", minute: "2-digit" }, fallback);
}
